import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg, initials } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/core/AppLayout";
import { UsernameForm } from "@/components/core/UsernameForm";
import { NotificationsTab } from "@/components/core/NotificationsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { OnboardingReopen } from "@/components/core/Onboarding";

export default function ProfilePage() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "notifiche" ? "notifiche" : "account";
  useEffect(() => { if (tab === "notifiche") api.post("/onboarding/notifiche-viste").catch(() => {}); }, [tab]);
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (f.next !== f.confirm) return toast.error("Le password non coincidono");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password aggiornata");
      setF({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const F = (k, label, testid) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      <Input type="password" required value={f[k]} onChange={set(k)} className="rounded-xl h-11" data-testid={testid} />
    </div>
  );

  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Account" title="Il mio profilo" />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })}>
        <TabsList className="bg-[#8FD0FB] rounded-full p-1 h-auto mb-6">
          {[["account", "Account"], ["notifiche", "Notifiche"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} className="rounded-full px-5 py-2 font-bold text-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid={`profile-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="notifiche"><NotificationsTab /></TabsContent>
        <TabsContent value="account">
      <div className="grid grid-cols-[1fr_1.3fr] gap-6 max-w-5xl">
        <Panel className="p-8 core-rise core-delay-1 bg-[#0A1128] border-[#0A1128] core-grain">
          <div className="h-20 w-20 rounded-2xl bg-[#E8173A] text-white text-2xl font-display font-black flex items-center justify-center shadow-lg">{initials(user.nome, user.cognome)}</div>
          <div className="font-display text-3xl font-black text-white mt-6" data-testid="profile-name">{user.nome} {user.cognome}</div>
          <div className="text-white/70 mt-1" data-testid="profile-email">{user.email}</div>
          {user.username && <div className="text-white/70 text-sm mt-0.5" data-testid="profile-username">@{user.username}</div>}
          <span className="inline-block mt-5 text-xs font-bold uppercase tracking-wider rounded-full bg-white/10 text-white px-3 py-1" data-testid="profile-role">{user.role === "admin" ? "Amministratore" : "Socio"}</span>
          <div className="[&_button]:text-white/80 [&_button:hover]:text-white"><OnboardingReopen /></div>
        </Panel>
        <Panel className="p-8 core-rise core-delay-2">
          <h3 className="font-display text-xl font-extrabold text-[#0A1128] flex items-center gap-2 mb-6"><KeyRound className="h-5 w-5 text-[#E8173A]" />Cambia password</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            {F("current", "Password attuale", "profile-current-password")}
            {F("next", "Nuova password", "profile-new-password")}
            {F("confirm", "Conferma nuova password", "profile-confirm-password")}
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6 h-11 font-bold" data-testid="profile-change-password-submit">{saving ? "Aggiornamento..." : "Aggiorna password"}</Button>
          </form>
        </Panel>
      </div>
      <UsernameForm />
        </TabsContent>
      </Tabs>
    </div>
  );
}
